import { FluzElementsError } from "./errors.js";
import { GOOGLE_FONTS } from "./googleFonts.generated.js";

export type AllowedStyleProperty = "color" | "fontSize" | "fontFamily" | "fontWeight";
type PatternValidatedProperty = Exclude<AllowedStyleProperty, "fontFamily">;

export type StyleAllowlist = Partial<Record<AllowedStyleProperty, string>>;

export { GOOGLE_FONTS };

// Common web-safe / OS font stacks. These render immediately with no remote
// load; anything not in this list must be an exact Google Fonts family name
// (see googleFonts.generated.ts) so the frame knows what @font-face to fetch.
export const SYSTEM_FONTS: readonly string[] = [
  "system-ui",
  "-apple-system",
  "BlinkMacSystemFont",
  "Segoe UI",
  "Helvetica Neue",
  "Helvetica",
  "Arial",
  "Verdana",
  "Tahoma",
  "Trebuchet MS",
  "Georgia",
  "Times New Roman",
  "Times",
  "Courier New",
  "Menlo",
  "Consolas",
  "monospace",
  "serif",
  "sans-serif",
  "cursive",
  "fantasy",
];

const ALLOWED_FONT_FAMILIES = new Set<string>([...SYSTEM_FONTS, ...GOOGLE_FONTS]);

export function isAllowedFontFamily(value: string): boolean {
  return ALLOWED_FONT_FAMILIES.has(value);
}

// Regex sources (not RegExp objects) so frame-host's frame documents can
// embed and reconstruct the identical patterns client-side, keeping parent
// and child validation in lockstep instead of duplicated by hand. fontFamily
// isn't pattern-validated -- it's checked against the exact allowlist above
// on both sides instead, since "any valid CSS font-family string" is too
// broad a surface and doesn't tell the frame whether a font needs loading.
export const STYLE_VALIDATION_PATTERNS: Record<PatternValidatedProperty, string> = {
  color: "^(#[0-9a-fA-F]{3}|#[0-9a-fA-F]{6}|rgb\\(\\s*\\d{1,3}\\s*,\\s*\\d{1,3}\\s*,\\s*\\d{1,3}\\s*\\)|[a-zA-Z]{3,20})$",
  fontSize: "^\\d{1,3}(px|pt|em|rem)$",
  fontWeight: "^(normal|bold|[1-9]00)$",
};

const VALIDATORS: Record<PatternValidatedProperty, RegExp> = Object.fromEntries(
  Object.entries(STYLE_VALIDATION_PATTERNS).map(([property, pattern]) => [property, new RegExp(pattern)]),
) as Record<PatternValidatedProperty, RegExp>;

export function validateStyle(styles: StyleAllowlist): StyleAllowlist {
  const validated: StyleAllowlist = {};

  for (const [property, value] of Object.entries(styles)) {
    if (typeof value !== "string") {
      throw new FluzElementsError("INVALID_STYLE", `Invalid value for style property "${property}"`);
    }

    if (property === "fontFamily") {
      if (!isAllowedFontFamily(value)) {
        throw new FluzElementsError("INVALID_STYLE", `Unsupported font family: ${value}`);
      }
      validated.fontFamily = value;
      continue;
    }

    const validator = VALIDATORS[property as PatternValidatedProperty];
    if (!validator) {
      throw new FluzElementsError("INVALID_STYLE", `Unsupported style property: ${property}`);
    }
    if (!validator.test(value)) {
      throw new FluzElementsError("INVALID_STYLE", `Invalid value for style property "${property}"`);
    }
    validated[property as PatternValidatedProperty] = value;
  }

  return validated;
}
